import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  chakra,
  Field,
  Flex,
  IconButton,
  SimpleGrid,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import {
  SelectInput,
  ColorPickerInput,
  Button as ButtonComponent,
  InputField,
  TextareaField,
} from '@themes/aegix';
import { HiTrash } from 'react-icons/hi2';
import { DrawerComponent } from '../ui/drawerComponent';
import type {
  PlanLabelTemplate,
  PlanLabelTemplateWrite,
} from '../../types/ReunificationTypes';
import {
  LABEL_TYPE_OPTIONS,
  LABEL_SIZE_OPTIONS,
} from '../../types/ReunificationTypes';
import {
  useCreateLabelTemplate,
  useUpdateLabelTemplate,
  useDeleteLabelTemplate,
} from '../../hooks/queries/useReunification';
import { ApiValidationError } from '../../lib/http-client';
import { toaster } from '../ui/toaster';
import { useLabelTemplatesCrud } from './hooks/useLabelTemplatesCrud';

// ---------------------------------------------------------------------------
// Label Template Row (table row with inline quantity editing)
// ---------------------------------------------------------------------------

function LabelTemplateRow({
  label,
  templateId,
}: {
  label: PlanLabelTemplate;
  templateId: string;
}) {
  const { t } = useTranslation();
  const updateMutation = useUpdateLabelTemplate(templateId);
  const deleteMutation = useDeleteLabelTemplate(templateId);

  return (
    <Table.Row borderBottomWidth="1px" borderColor="border.primary">
      <Table.Cell py={3}>
        <Text fontSize="sm" color="text.primary">
          {label.name}
        </Text>
      </Table.Cell>
      <Table.Cell py={3}>
        <Text fontSize="sm" color="text.primary">
          {label.label_type_display}
        </Text>
      </Table.Cell>
      <Table.Cell py={3}>
        <Text fontSize="sm" color="text.primary">
          {label.size_display}
        </Text>
      </Table.Cell>
      <Table.Cell py={3}>
        <InputField
          size="sm"
          type="number"
          min={0}
          width="70px"
          aria-label={`Quantity needed for ${label.name}`}
          defaultValue={label.quantity_needed}
          onBlur={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val !== label.quantity_needed) {
              updateMutation.mutate({
                id: label.public_id,
                data: { quantity_needed: val },
              });
            }
          }}
        />
      </Table.Cell>
      <Table.Cell py={3}>
        <IconButton
          aria-label={t('reunification.removeLabel', 'Remove label')}
          size="xs"
          variant="ghost"
          colorPalette="red"
          onClick={() => deleteMutation.mutate(label.public_id)}
        >
          <HiTrash />
        </IconButton>
      </Table.Cell>
    </Table.Row>
  );
}

// ---------------------------------------------------------------------------
// Add Label Drawer (RHF form)
// ---------------------------------------------------------------------------

interface AddLabelFormValues {
  name: string;
  label_type: string;
  size: string;
  content_template: string;
  color_scheme: string;
  quantity_needed: number | '';
  placement_guidance: string;
}

const addLabelDefaults: AddLabelFormValues = {
  name: '',
  label_type: '',
  size: '',
  content_template: '',
  color_scheme: '',
  quantity_needed: 1,
  placement_guidance: '',
};

function AddLabelDrawer({
  isOpen,
  onClose,
  templateId,
}: {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
}) {
  const { t } = useTranslation();
  const createMutation = useCreateLabelTemplate(templateId);

  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors },
  } = useForm<AddLabelFormValues>({
    defaultValues: addLabelDefaults,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (isOpen) {
      reset(addLabelDefaults);
    }
  }, [isOpen, reset]);

  const onSubmit = (values: AddLabelFormValues) => {
    const payload: PlanLabelTemplateWrite = {
      name: values.name,
      label_type: values.label_type,
      size: values.size || undefined,
      content_template: values.content_template || undefined,
      color_scheme: values.color_scheme || undefined,
      quantity_needed:
        values.quantity_needed === '' ? undefined : values.quantity_needed,
      placement_guidance: values.placement_guidance || undefined,
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        onClose();
      },
      onError: (err) => {
        if (err instanceof ApiValidationError) {
          for (const [field, messages] of Object.entries(err.fieldErrors)) {
            setError(field as keyof AddLabelFormValues, {
              message: messages.join(', '),
            });
          }
        }
        toaster.create({
          title: t('reunification.failedCreateLabel', 'Failed to create label'),
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t('reunification.addLabel', 'Add Label')}
      size="sm"
      footer={
        <SimpleGrid columns={2} gap={4} width="100%">
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={onClose}
            data-e2e="cancel-add-label-button"
          >
            {t('common.cancel', 'Cancel')}
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="submit"
            form="add-label-form"
            data-e2e="confirm-add-label-button"
          >
            {t('common.add', 'Add')}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <form id="add-label-form" onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={4} align="stretch">
          <Field.Root invalid={!!errors.name}>
            <Field.Label htmlFor="label-name">{t('common.name', 'Name')}</Field.Label>
            <InputField
              id="label-name"
              placeholder={t('reunification.enterLabelName', 'Enter label name')}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'label-name-error' : undefined}
              {...register('name')}
              data-e2e="label-name-input"
            />
            {errors.name && (
              <Field.ErrorText id="label-name-error" data-e2e="label-name-error">
                {errors.name.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.label_type}>
            <Controller
              name="label_type"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="label-type"
                  name="label-type"
                  label={t('reunification.labelType', 'Label Type')}
                  value={field.value}
                  onChange={(val: string) => field.onChange(val)}
                  option={LABEL_TYPE_OPTIONS}
                  placeholder={t('reunification.selectLabelType', 'Select label type')}
                  e2eTitle="label-type-select"
                  invalid={!!errors.label_type}
                />
              )}
            />
            {errors.label_type && (
              <Field.ErrorText id="label-type-error" data-e2e="label-type-error">
                {errors.label_type.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.size}>
            <Controller
              name="size"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="label-size"
                  name="label-size"
                  label={t('reunification.size', 'Size')}
                  value={field.value}
                  onChange={(val: string) => field.onChange(val)}
                  option={LABEL_SIZE_OPTIONS}
                  placeholder={t('reunification.selectSize', 'Select size')}
                  e2eTitle="label-size-select"
                  invalid={!!errors.size}
                />
              )}
            />
            {errors.size && (
              <Field.ErrorText id="label-size-error" data-e2e="label-size-error">
                {errors.size.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.content_template}>
            <Field.Label htmlFor="label-content-template">{t('reunification.contentTemplate', 'Content Template')}</Field.Label>
            <TextareaField
              id="label-content-template"
              placeholder={t('reunification.labelContentTemplate', 'Label content template')}
              rows={3}
              aria-invalid={!!errors.content_template}
              aria-describedby={errors.content_template ? 'label-content-template-error' : undefined}
              {...register('content_template')}
              data-e2e="label-content-template-textarea"
            />
            {errors.content_template && (
              <Field.ErrorText id="label-content-template-error" data-e2e="label-content-template-error">
                {errors.content_template.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.color_scheme}>
            <Field.Label>{t('reunification.colorScheme', 'Color Scheme')}</Field.Label>
            <Controller
              name="color_scheme"
              control={control}
              render={({ field }) => (
                <Flex gap={4} alignItems="center">
                  <Box>
                    <chakra.label htmlFor="foreground-color" fontSize="sm" mb={1} color="text.secondary" display="block">
                      {t('reunification.foreground', 'Foreground')}
                    </chakra.label>
                    <ColorPickerInput
                      id="foreground-color"
                      aria-label="Foreground color hex value"
                      value={field.value.split('-')[0] || ''}
                      onChange={(fg) => {
                        const bg = field.value.split('-')[1] || '';
                        field.onChange(bg ? `${fg}-${bg}` : fg);
                      }}
                      aria-invalid={!!errors.color_scheme}
                      aria-describedby={errors.color_scheme ? 'label-color-scheme-error' : undefined}
                    />
                  </Box>
                  <Box>
                    <chakra.label htmlFor="background-color" fontSize="sm" mb={1} color="text.secondary" display="block">
                      {t('reunification.background', 'Background')}
                    </chakra.label>
                    <ColorPickerInput
                      id="background-color"
                      aria-label="Background color hex value"
                      value={field.value.split('-')[1] || ''}
                      onChange={(bg) => {
                        const fg = field.value.split('-')[0] || '';
                        field.onChange(fg ? `${fg}-${bg}` : bg);
                      }}
                      aria-invalid={!!errors.color_scheme}
                      aria-describedby={errors.color_scheme ? 'label-color-scheme-error' : undefined}
                    />
                  </Box>
                </Flex>
              )}
            />
            {errors.color_scheme && (
              <Field.ErrorText id="label-color-scheme-error" data-e2e="label-color-scheme-error">
                {errors.color_scheme.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.quantity_needed}>
            <Field.Label htmlFor="label-quantity">{t('reunification.quantityNeeded', 'Quantity Needed')}</Field.Label>
            <InputField
              id="label-quantity"
              type="number"
              min={0}
              placeholder="1"
              aria-invalid={!!errors.quantity_needed}
              aria-describedby={errors.quantity_needed ? 'label-quantity-error' : undefined}
              {...register('quantity_needed', { valueAsNumber: true })}
              data-e2e="label-quantity-input"
            />
            {errors.quantity_needed && (
              <Field.ErrorText id="label-quantity-error" data-e2e="label-quantity-error">
                {errors.quantity_needed.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.placement_guidance}>
            <Field.Label htmlFor="label-placement-guidance">{t('reunification.placementGuidance', 'Placement Guidance')}</Field.Label>
            <TextareaField
              id="label-placement-guidance"
              placeholder={t('reunification.placementGuidancePlaceholder', 'Where should this label be placed?')}
              rows={3}
              aria-invalid={!!errors.placement_guidance}
              aria-describedby={errors.placement_guidance ? 'label-placement-guidance-error' : undefined}
              {...register('placement_guidance')}
              data-e2e="label-placement-guidance-textarea"
            />
            {errors.placement_guidance && (
              <Field.ErrorText id="label-placement-guidance-error" data-e2e="label-placement-guidance-error">
                {errors.placement_guidance.message}
              </Field.ErrorText>
            )}
          </Field.Root>
        </VStack>
      </form>
    </DrawerComponent>
  );
}

// ---------------------------------------------------------------------------
// Main Section
// ---------------------------------------------------------------------------

export function LabelTemplatesSection({ templateId }: { templateId: string }) {
  const { t } = useTranslation();
  const { labels, isLoading, drawerOpen, setDrawerOpen } =
    useLabelTemplatesCrud(templateId);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontWeight="semibold" fontSize="sm">
          {t('reunification.labelTemplates', 'Label Templates')}
        </Text>
        <ButtonComponent
          visual="secondary"
          size="sm"
          type="button"
          onClick={() => setDrawerOpen(true)}
          data-e2e="add-label-button"
        >
          {t('reunification.addLabel', 'Add Label')}
        </ButtonComponent>
      </Flex>

      {isLoading ? (
        <Spinner size="sm" />
      ) : labels && labels.length > 0 ? (
        <Box
          borderWidth="1px"
          borderColor="border.primary"
          borderRadius="lg"
          overflow="hidden"
          bg="surface.primary"
          maxHeight="320px"
          overflowY="auto"
        >
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row bg="surface.secondary">
                <Table.ColumnHeader>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {t('common.name', 'Name')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="130px">
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {t('common.type', 'Type')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="100px">
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {t('reunification.size', 'Size')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="90px">
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {t('reunification.qty', 'Qty')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="50px" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {labels.map((label) => (
                <LabelTemplateRow
                  key={label.public_id}
                  label={label}
                  templateId={templateId}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      ) : (
        <Text fontSize="sm" color="text.secondary">
          {t('reunification.noLabelTemplatesConfigured', 'No label templates configured')}
        </Text>
      )}

      <AddLabelDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        templateId={templateId}
      />
    </Box>
  );
}
